import * as Preact from "preact"
import Objdict from "objdict"
import {nanoid} from "nanoid"

import App from "@frog/source/models/App.js"
import World from "@frog/source/models/World.js"
import Stage from "@frog/source/models/Stage.js"
import Zone from "@frog/source/models/Zone.js"
import Step from "@frog/source/models/Step.js"
import Random from "@frog/source/utility/Random.js"
import Navigation from "@frog/source/engine/navigating/Navigation.js"

import * as PlotSteps from "@frog/source/data/plot/steps.js"
import * as PlotConditions from "@frog/source/data/plot/conditions.js"
import * as PlotScripts from "@frog/source/data/plot/scripts.js"
import {RollPomp} from "@frog/source/data/games/dice/scripts/Rolling.js"
import HandleStatusEffects from "@frog/source/data/games/dice/scripts/HandleStatusEffects.js"

import LootModal from "@frog/source/data/games/dice/views/LootModal.jsx"
import RollingModal from "@frog/source/data/games/dice/views/RollingModal.jsx"
import LearnToRightClickModal from "@frog/source/data/games/dice/views/LearnToRightClickModal.jsx"
import RollingHud from "@frog/source/data/games/dice/views/hud/RollingHud.jsx"
import FrogModal from "@frog/source/data/games/dice/views/FrogModal.jsx"

import * as DiceFlow from "@frog/source/data/games/dice/scripts/Flow.js"

const ENCOUNTER_SPECS = [
    {
        "encounterKey": "1",
        "unitJobKeys": ["monster/slime/fire", "monster/slime/water", "monster/slime/fire", "monster/slime/earth"],
        "y": 0,
    },
    {
        "encounterKey": "2",
        "unitJobKeys": ["monster/skeleton", "monster/skeleton"],
        "y": -1,
    },
    {
        "encounterKey": "3",
        "unitJobKeys": ["monster/slime/fire", "monster/slime/fire", "monster/slime/earth"],
        "goodUnitKeys": ["frog"],
        "y": 0,
    },
    {
        "encounterKey": "4",
        "unitJobKeys": ["monster/skeleton", "monster/skeleton", "monster/goblin", "monster/goblin"],
        "y": +1,
    },
    {
        "encounterKey": "5",
        "unitJobKeys": ["monster/slime/water", "monster/slime/water", "monster/slime/queen"],
        "y": 0,
    },
]

export default {
    "slug": "chX",
    "stage": {
        "configuration": {
            "isRenderingEncounters": true,
        },
        "images": [
            // {
            //     "url": require("../bg.png"),
            //     "position": {"x": 1.5, "y": 5, "z": 1},
            //     "width": 16, "height": 9,
            //     // "screenspace": "camera",
            // },
            // {
            //     "url": require("./map_26x21.png"),
            //     "position": {"x": 0, "y": 0, "z": 2},
            //     "width": 26, "height": 21,
            //     "isSquiggling": true,
            // }
        ],
        "units": require("./units.json"),
        "world": World.parse({
            "blocks": require("./blocks.json"),
            "spots": require("./spots.json"),
            "encounters": require("./encounters.json"),
            "width": 64,
            "height": 32,
        }),
        "hud": RollingHud,
        "scripts": [
            {
                "trigger": "turn:0:start",
                "events": [
                    function GenerateDungeon(stage) {
                        let returnedSpots = []
                        let returnedEncounters = []
                        let returnedUnits = []

                        // Filter for all non-numbered encounters.
                        // These are our custom room templates!!
                        let encounters = Object.values(stage.encounters).filter((encounter) => {
                            return Number.isNaN(Number.parseFloat(encounter.key))
                        })

                        // Mark any and all spots that were defined with the
                        // encounter, so they can be moved with them.
                        encounters.forEach((encounter) => {
                            encounter.spots = []
                            Object.values(stage.world.spots).forEach((spot) => {
                                if(Zone.isInBounds(encounter, spot.position)) {
                                    encounter.spots.push(spot)
                                }
                            })
                        })

                        // Most of these encounters were defined wherever
                        // across the map - normalize their position
                        // to 0x0 to make it easier  to reposition them.
                        encounters = encounters.map((encounter) => {
                            return translateEncounter(encounter, {
                                "x": -1 * encounter.x1,
                                "y": -1 * encounter.y1,
                            })
                        })

                        // Randomize the order of the rooms.
                        encounters = stage.random.shuffle(encounters)

                        let currentPosition = {"x": 7, "y": 7}
                        Object.values(ENCOUNTER_SPECS).forEach((spec) => {
                            let returnedEncounter = encounters.pop()
                            returnedEncounter = translateEncounter(returnedEncounter, {
                                "x": currentPosition.x,
                                "y": currentPosition.y + (spec.y || 0)
                            })
                            returnedEncounter.key = spec.encounterKey
                            returnedEncounters.push(returnedEncounter)
                            returnedSpots = returnedSpots.concat(returnedEncounter.spots)

                            let badSpots = returnedEncounter.spots.filter((spot) => spot.tag == "badspawn")
                            let goodSpots = returnedEncounter.spots.filter((spot) => spot.tag == "goodspawn")
                            badSpots = stage.random.shuffle(badSpots)
                            goodSpots = stage.random.shuffle(goodSpots)

                            if(spec.unitJobKeys != undefined) {
                                spec.unitJobKeys.map((unitJobKey) => {
                                    const spot = badSpots.pop()
                                    if(spot == undefined) return
                                    returnedUnits.push({
                                        "key": "spawned" + nanoid(),
                                        "jobKey": unitJobKey,
                                        "position": {
                                            "x": spot.position.x,
                                            "y": spot.position.y,
                                            "z": spot.position.z
                                        }
                                    })
                                })
                            }
                            if(spec.goodUnitKeys != undefined) {
                                spec.goodUnitKeys.forEach((unitKey) => {
                                    const spot = goodSpots.pop()
                                    const unit = stage.player.party.units[unitKey]
                                    if(spot == undefined) return
                                    if(unit == undefined) return
                                    returnedUnits.push({
                                        ...unit,
                                        "position": {
                                            "x": spot.position.x,
                                            "y": spot.position.y,
                                            "z": spot.position.z
                                        }
                                    })
                                })
                            }

                            currentPosition.x += returnedEncounter.width
                        })

                        // Not sure why we support this in the schema, but for
                        // whatever reason the encounters must each have themself
                        // as encounter.zone...?
                        returnedEncounters.forEach((encounter) => {
                            encounter.zone = encounter
                        })

                        return [
                            Step.deltastage({
                                "encounters": Objdict.convert(returnedEncounters),
                                "spots": Objdict.convert(returnedSpots),
                            }),
                            Step.deltastage({
                                "units": Objdict.convert(returnedUnits),
                            })
                        ]

                        // TODO: We should probably set the scripts
                        // dynamically, for however many encounters
                        // we list in the spec.
                    },
                    PlotSteps.ReportStartingAnalytics(),
                    PlotSteps.SpawnPlayerParty(),
                    function EquipInitialItems(stage) {
                        return {
                            "deltastages": Object.values(stage.units).map((unit) => {
                                return {
                                    "unit": {
                                        "key": unit.key,
                                        "equippedItem": unit.diceFaces[0],
                                        "primaryItem": unit.diceFaces[0],
                                    }
                                }
                            })
                        }
                    },
                    PlotSteps.PlayAudio("Ambience"),
                    PlotSteps.PlayStinger("Stinger:Start"),
                    PlotSteps.ChapterStart([
                        "Level 0", "Dungeon of the Slime Queen"
                    ]),

                    PlotSteps.PlayAudio("Music:1A"),
                    Step.deltastage({
                        "player": {
                            "phase": "playing",
                            "zoomout": 1,
                        },
                    }),

                    // LearnToRightClickModal(),
                ]
            },
            {
                "trigger": "turn:every-odd:start",
                "events": [
                    PlotSteps.PlayStinger("Stinger:EnemyTurn"),
                    {
                        "duration": 1200,
                        "deltastages": [
                            {
                                "dialogue": {
                                    "type": "TurnStart",
                                    "data": {"affiliation": "bad"},
                                }
                            }
                        ]
                    }
                ]
            },
            {
                "trigger": "turn:every-odd:end",
                "events": [
                    HandleStatusEffects(),
                ]
            },
            {
                "condition": (stage) => {
                    return stage.turn.number % 2 == 1
                        && stage.turn.phase == "end"
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"}) != true
                },
                "events": [
                    RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 1
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.PlayAudio("Music:1B"),
                    PlotSteps.HealPlayerParty({"percent": 1}),
                    PlotSteps.HealPlayerParty({"percent": 0.5, "isDead": true}),
                    DiceFlow.MoveToNewEncounter({"nextEncounterKey": 2, "spotTag": "goodspawn"}),
                    ...LootModal(),
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 2
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.PlayAudio("Music:2"),
                    PlotSteps.HealPlayerParty({"percent": 1}),
                    PlotSteps.HealPlayerParty({"percent": 0.5, "isDead": true}),
                    DiceFlow.MoveToNewEncounter({"nextEncounterKey": 3, "spotTag": "goodspawn"}),
                    ...LootModal(),
                    FrogModal(),
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 3
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.PlayAudio("Music:1B"),
                    PlotSteps.HealPlayerParty({"percent": 1}),
                    PlotSteps.HealPlayerParty({"percent": 0.5, "isDead": true}),
                    DiceFlow.MoveToNewEncounter({"nextEncounterKey": 4, "spotTag": "goodspawn"}),
                    ...LootModal(),
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 4
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.PlayAudio("Music:Boss"),
                    PlotSteps.HealPlayerParty({"percent": 1}),
                    PlotSteps.HealPlayerParty({"percent": 0.5, "isDead": true}),
                    DiceFlow.MoveToNewEncounter({"nextEncounterKey": 5, "spotTag": "goodspawn"}),
                    ...LootModal(),
                    ...RollPomp(),
                ]
            },
            {
                "isWinCondition": true,
                "condition": (stage) => {
                    return stage.currentEncounterKey == 5
                        && PlotConditions.areAllAffiliatedUnitsDead(stage, {"affiliation": "bad"})
                },
                "events": [
                    PlotSteps.ReportSuccessAnalytics(),
                    PlotSteps.PlayAudio("Stinger:Success"),
                    PlotSteps.ChapterSuccess(),
                    PlotSteps.TransitionStart({"isUnskippable": true}),
                    PlotSteps.Wait(250, {"isUnskippable": true}),
                    PlotSteps.WaitingModal({"isUnskippable": true, "duration": 2000}),
                    PlotSteps.KillAudio({"label": "ambience"}),
                    PlotSteps.Go("/games/dice"),
                ]
            },
            {

                "condition": function(stage) {
                    return PlotConditions.areAllAffiliatedUnitsDefeated(stage, {"affiliation": stage.player.affiliation})
                },
                "events": [
                    PlotSteps.PlayAudio("Stinger:Failure"),
                    PlotSteps.KillAudio({"label": "ambience"}),
                    PlotSteps.ReportFailureAnalytics(),
                    PlotSteps.ChapterFailure(),
                    {"func": () => App.game.chapter.restart()},
                ]
            },
        ]
    },
}



function translateEncounter(encounter, {x, y}) {
    return {
        ...encounter,

        "center": {
            "x": encounter.center.x + x,
            "y": encounter.center.x + y,
        },
        "spots": !!encounter.spots && encounter.spots.map((spot) => {
            return {
                ...spot,
                "position": {
                    "x": spot.position.x + x,
                    "y": spot.position.y + y,
                    "z": spot.position.z,
                }
            }
        }),
        "points": encounter.points.map((point) => {
            return {
                ...point,
                "x": point.x + x,
                "y": point.y + y
            }
        }),
        "x": encounter.x + x,
        "y": encounter.y + y,
        "x1": encounter.x1 + x,
        "y1": encounter.y1 + y,
        "x2": encounter.x2 + x,
        "y2": encounter.y2 + y,
        "zone": undefined,
    }
}
